/**
 * DR Vitamin live search.
 *
 * Shopify's AJAX Predictive Search API does not support Arabic as a buyer locale and answers 417
 * on this storefront, so the theme's <predictive-search> silently fell back to a static popular
 * list. This drives the NORMAL storefront search engine through the Section Rendering API instead,
 * which does index our Arabic tag vocabulary.
 *
 * Two zones, following a mature marketplace's autocomplete model:
 *   1. term suggestions  — matched client-side against the catalogue vocabulary
 *   2. product results   — rendered server-side by the drv-live-search section
 *
 * Deliberate behaviours:
 *   • empty query  -> show whatever static/trending markup the header already renders
 *   • active query -> ONLY real matches; the static list is hidden so it can never read as a match
 *   • zero results -> "هل تقصد؟" offers the nearest REAL catalogue terms, never popular filler
 *   • debounce + AbortController + latest-query-wins so typing cannot stampede the origin
 *   • small LRU cache: re-typing or backspacing costs no request
 *   • full keyboard control: ArrowDown/ArrowUp/Enter/Escape
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var MIN_LEN = 2;
  var CACHE_MAX = 30;
  var MAX_SUGGESTIONS = 6;

  /**
   * Suggestion-zone vocabulary, held in BOTH languages on purpose.
   *
   * The owner's rule separates two things that Shopify's `| t` cannot separate:
   *   - the STOREFRONT locale decides the language of the page and of the product page a click
   *     lands on. That is theme chrome and it belongs in locales/*.json (see drv-live-search.liquid).
   *   - the QUERY language decides how a *suggestion* is presented, because the suggestion is an
   *     echo of what the visitor typed. Someone typing "Omega 3" on the Arabic storefront is
   *     reading English at that moment and expects to recognise their own word coming back.
   *
   * `| t` can only ever yield the page's locale, so the bilingual pair lives here instead of
   * being faked with a translation key that would return the wrong language by definition.
   */
  /* 2026-09-16 (owner): headings and kind names follow the STOREFRONT locale; only the echo of a term may follow the query. */
  var GROUPS = [
    { key: 'class', kinds: { 'class': 1 }, ar: 'الفئات', en: 'Categories', attr: 'g-categories' },
    { key: 'brand', kinds: { brand: 1 }, ar: 'البراندات', en: 'Brands', attr: 'g-brands' },
    { key: 'goal', kinds: { goal: 1, audience: 1 }, ar: 'احتياجاتك', en: 'Your needs', attr: 'g-needs' }
  ];
  var DID_YOU_MEAN = { ar: 'هل تقصد؟', en: 'Did you mean?' };
  var SHOWING = { ar: 'نتائج لـ «__TERMS__»', en: 'Showing results for “__TERMS__”' };

  // Arabic block + Arabic Supplement/Extended-A. Latin, digits and symbols fall through to 'en'.
  var ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  function queryLang(q) { return ARABIC_RE.test(String(q)) ? 'ar' : 'en'; }

  // The storefront locale, for the chrome that must follow the PAGE rather than the query.
  function pageLang() {
    var l = (window.Shopify && window.Shopify.locale) ||
            document.documentElement.getAttribute('lang') || 'ar';
    return String(l).toLowerCase().indexOf('ar') === 0 ? 'ar' : 'en';
  }

  // Storefront-locale chrome, rendered once by Liquid onto the vocab carrier element.
  function chromeString(name, fallback) {
    var el = document.querySelector('[data-drv-vocab-url]');
    var v = el && el.getAttribute('data-drv-' + name);
    return v || fallback;
  }

  function localeRoot() {
    // Shopify exposes the locale-aware root (e.g. "/" or "/en/"); never hardcode it.
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
               (window.routes && window.routes.root_url) || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  /* ---------------- vocabulary ---------------- */

  // Must stay identical to the normaliser in build-suggest-vocab.mjs.
  function norm(s) {
    return String(s)
      .toLowerCase()
      .replace(/[\u064B-\u0652\u0640]/g, '')
      .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627')
      .replace(/\u0649/g, '\u064A')
      .replace(/\u0629/g, '\u0647')
      .replace(/[\u0660-\u0669]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
      .replace(/[^\p{L}\p{N}+]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* 2026-09-16: a known typo / zero-result synonym → the concept query ([term index, 't'|'s', optional query]). */
  function rewriteFor(q) {
    if (!vocab || !vocab.rw) return null;
    var k = norm(q), hit = vocab.rw[k];
    if (!hit) return null;
    var t = vocab.terms[hit[0]];
    var target = hit[2] || (t && t.q);
    if (!target || norm(target) === k) return null;
    return { target: target, kind: hit[1], term: t };
  }

  function editDistance(a, b, max) {
    if (a === b) return 0;
    var m = a.length, n = b.length;
    if (Math.abs(m - n) > max) return max + 1;
    if (!m) return n;
    if (!n) return m;
    var prev = new Array(n + 1), cur = new Array(n + 1), i, j;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= n; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;     // bail out early
      for (j = 0; j <= n; j++) prev[j] = cur[j];
    }
    return prev[n];
  }

  var vocab = null;
  var vocabPromise = null;
  function loadVocab() {
    if (vocab) return Promise.resolve(vocab);
    if (vocabPromise) return vocabPromise;
    var el = document.querySelector('[data-drv-vocab-url]');
    var url = el && el.getAttribute('data-drv-vocab-url');
    if (!url) return Promise.resolve(null);
    vocabPromise = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || !j.terms) return null;
        j.terms.forEach(function (t) { t._m = (t.m || []).map(norm); });
        vocab = j;
        return vocab;
      })
      .catch(function () { return null; });
    return vocabPromise;
  }

  /**
   * Scores catalogue terms against the query.
   * exact 100 · prefix 80 · contains 60 · near-miss 45..30 (this is the "هل تقصد؟" band).
   */
  function scoreTerms(q) {
    if (!vocab) return [];
    var nq = norm(q);
    if (nq.length < MIN_LEN) return [];
    var out = [];
    var words = nq.split(' '), grams = {};
    for (var gi = 0; gi < words.length; gi++) {
      if (words[gi].length >= 3) grams[words[gi]] = 1;
      if (gi < words.length - 1) grams[words[gi] + ' ' + words[gi + 1]] = 1;
    }
    var multi = words.length > 1;
    vocab.terms.forEach(function (t) {
      if (t.k === 'form') return;
      var best = 0, corrected = false;
      for (var i = 0; i < t._m.length; i++) {
        var k = t._m[i], s = 0, fuzzy = false;
        if (k === nq) s = 100;
        else if (k.indexOf(nq) === 0) s = 80 - Math.min(15, k.length - nq.length);
        else if (k.indexOf(nq) > 0) s = 60;
        else {
          var budget = nq.length <= 4 ? 1 : nq.length <= 8 ? 2 : 3;
          var d = editDistance(k, nq, budget);
          if (d <= budget) { s = 45 - (d - 1) * 8; fuzzy = true; }
        }
        if (multi && s === 0 && grams[k]) s = 70;
        if (s > best) { best = s; corrected = fuzzy; }
      }
      if (best > 0) out.push({ term: t, score: best + Math.min(8, (t.n || 0) / 20), corrected: corrected });
    });
    out.sort(function (a, b) { return b.score - a.score; });
    return out;
  }

  function suggestionHref(t) {
    // A term with a real collection goes to that curated page; otherwise to a search.
    if (t.c) return localeRoot() + 'collections/' + t.c;
    return localeRoot() + 'search?q=' + encodeURIComponent(t.t) + '&type=product';
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  /**
   * Keeps a Latin/number run readable inside an Arabic label.
   *
   * UAX#9 rule W2 retypes a European Number as an ARABIC Number when the nearest preceding strong
   * character is an Arabic letter. The separators then resolve to the paragraph direction, so
   * "أوميغا 3-6-9" paints as "أوميغا 9-6-3" - measurably, not theoretically.
   * Isolating each Latin/digit run restores its own direction. A label that is entirely Latin is
   * unaffected, because a leading strong L already keeps its digits European.
   *
   * Escaping happens per fragment: escaping first would let the regex match inside "&amp;".
   */
  function bidiIsolate(str) {
    // A label with no Arabic in it needs no help: its own leading strong L already governs.
    if (!ARABIC_RE.test(str)) return esc(str);
    var re = /[0-9A-Za-z][0-9A-Za-z.,\-+\/&'’]*/g;
    var out = '', last = 0, m;
    while ((m = re.exec(str)) !== null) {
      out += esc(str.slice(last, m.index)) + '<bdi dir="ltr">' + esc(m[0]) + '</bdi>';
      last = m.index + m[0].length;
    }
    return out + esc(str.slice(last));
  }

  /**
   * `lang` is the QUERY language, not the page language: the suggestion echoes the visitor's own
   * word back to them. Every vocabulary term carries both `t` (English) and `ar` (Arabic), so the
   * fallback is the other language rather than an empty row.
   */
  function renderSuggestions(list, heading, qlang) {
    if (!list.length) return '';
    var plang = pageLang();
    var html = '<div class="drv-ls__suggest-inner" lang="' + plang + '" dir="' + (plang === 'ar' ? 'rtl' : 'ltr') + '">';
    if (heading) html += '<p class="drv-ls__suggest-head">' + esc(heading) + '</p>';
    GROUPS.forEach(function (g) {
      var rows = list.filter(function (row) { return g.kinds[row.term.k]; }).slice(0, 3);
      if (!rows.length) return;
      html += '<p class="drv-ls__group">' + esc(chromeString(g.attr, g[plang])) + '</p><ul class="drv-ls__terms">';
      rows.forEach(function (row) {
        var t = row.term;
        var primary = plang === 'ar' ? (t.ar || t.t) : (t.t || t.ar);
        var echo = qlang !== plang ? (qlang === 'ar' ? t.ar : t.t) : '';
        html += '<li><a class="drv-ls__term" href="' + esc(suggestionHref(t)) + '">' +
          '<span class="drv-ls__term-name">' + bidiIsolate(primary) + '</span>' +
          (echo && norm(echo) !== norm(primary) ? '<span class="drv-ls__term-echo">' + bidiIsolate(echo) + '</span>' : '') +
          '</a></li>';
      });
      html += '</ul>';
    });
    return html + '</div>';
  }

  /* ---------------- component ---------------- */

  function LiveSearch(input, panel, staticPanel) {
    this.input = input;
    this.panel = panel;
    this.suggestZone = panel.querySelector('.drv-ls__suggest');
    this.resultZone = panel.querySelector('.drv-ls__results');
    this.staticPanel = staticPanel || null;
    this.controller = null;
    this.timer = null;
    this.seq = 0;
    this.cache = new Map();
    this.activeIndex = -1;
    this.bind();
  }

  LiveSearch.prototype.bind = function () {
    var self = this;
    this.input.addEventListener('input', function () { self.schedule(); });
    this.input.addEventListener('focus', function () { loadVocab(); self.schedule(); });
    this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });
    document.addEventListener('click', function (e) {
      if (!self.panel.contains(e.target) && e.target !== self.input) self.close();
    });
  };

  LiveSearch.prototype.links = function () {
    return Array.prototype.slice.call(this.panel.querySelectorAll('.drv-ls__term, .drv-ls__link, .drv-ls__all'));
  };

  LiveSearch.prototype.onKeydown = function (e) {
    if (e.key === 'Escape') { this.close(); this.input.blur(); return; }
    var items = this.links();
    if (!items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex += (e.key === 'ArrowDown' ? 1 : -1);
      if (this.activeIndex < 0) this.activeIndex = items.length - 1;
      if (this.activeIndex >= items.length) this.activeIndex = 0;
      var idx = this.activeIndex;
      items.forEach(function (n, i) { n.classList.toggle('is-active', i === idx); });
      var active = items[idx];
      active.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.activeIndex >= 0 && items[this.activeIndex]) {
      e.preventDefault();
      items[this.activeIndex].click();
    }
  };

  LiveSearch.prototype.schedule = function () {
    var self = this;
    clearTimeout(this.timer);
    this.timer = setTimeout(function () { self.run(); }, DEBOUNCE_MS);
  };

  LiveSearch.prototype.showStatic = function (show) {
    if (!this.staticPanel) return;
    this.staticPanel.style.display = show ? '' : 'none';
  };

  LiveSearch.prototype.close = function () {
    this.suggestZone.innerHTML = '';
    this.resultZone.innerHTML = '';
    this.panel.hidden = true;
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.showStatic(true);
  };

  LiveSearch.prototype.paintSuggestions = function (q, hadResults) {
    var scored = scoreTerms(q);
    if (!scored.length) { this.suggestZone.innerHTML = ''; return; }

    // The suggestion zone speaks the language the visitor typed in, whatever the storefront is.
    var lang = queryLang(q);
    var strong = scored.filter(function (r) { return !r.corrected; }).slice(0, MAX_SUGGESTIONS);
    if (hadResults) {
      // Alongside real results, only confident terms are worth offering.
      this.suggestZone.innerHTML = renderSuggestions(strong, null, lang);
    } else {
      // Nothing matched the query itself — offer the nearest REAL catalogue terms.
      var near = scored.slice(0, MAX_SUGGESTIONS);
      this.suggestZone.innerHTML = renderSuggestions(near, chromeString('dym', DID_YOU_MEAN[pageLang()]), lang);
    }
  };

  LiveSearch.prototype.run = function () {
    var q = (this.input.value || '').trim();

    // Empty / too-short query: fall back to the header's own trending markup.
    if (q.length < MIN_LEN) { this.close(); return; }

    // An active query must never sit beside static "popular" products.
    this.showStatic(false);
    this.panel.hidden = false;
    this.activeIndex = -1;

    var self = this;
    var paint = function (html, rewrote) {
      self.resultZone.innerHTML = html;
      var hadResults = !!self.resultZone.querySelector('.drv-ls__item');
      if (rewrote && hadResults) {
        var note = document.createElement('p');
        note.className = 'drv-ls__showing';
        note.textContent = chromeString('showing', SHOWING[pageLang()]).replace('__TERMS__', rewrote);
        self.resultZone.insertBefore(note, self.resultZone.firstChild);
        var all = self.resultZone.querySelector('.drv-ls__all');
        if (all) all.setAttribute('href', all.getAttribute('href') + '&oq=' + encodeURIComponent(q));
      }
      loadVocab().then(function () { self.paintSuggestions(q, hadResults || !!rewrote); });
    };

    var cacheKey = q;
    if (this.cache.has(cacheKey)) { var c0 = this.cache.get(cacheKey); paint(c0.html, c0.rewrote); return; }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    var mySeq = ++this.seq;

    this.panel.setAttribute('aria-busy', 'true');

    var signal = this.controller.signal;
    var fetchFor = function (term) {
      var url = localeRoot() + 'search?q=' + encodeURIComponent(term) +
        '&type=product&options[prefix]=last&section_id=drv-live-search';
      return fetch(url, { signal: signal }).then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status)); });
    };
    var hasItems = function (html) { return html.indexOf('drv-ls__item') !== -1; };

    loadVocab()
      .then(function () {
        var rw = rewriteFor(q);
        if (rw && rw.kind === 't') return fetchFor(rw.target).then(function (html) { return { html: html, rewrote: rw.target }; });
        return fetchFor(q).then(function (html) {
          if (hasItems(html) || !rw) return { html: html, rewrote: null };
          return fetchFor(rw.target).then(function (h2) { return hasItems(h2) ? { html: h2, rewrote: rw.target } : { html: html, rewrote: null }; });
        });
      })
      .then(function (res) {
        if (mySeq !== self.seq) return;            // a newer query already won
        self.cache.set(cacheKey, res);
        if (self.cache.size > CACHE_MAX) self.cache.delete(self.cache.keys().next().value);
        paint(res.html, res.rewrote);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (mySeq !== self.seq) return;
        // Error-safe: offer the full results page rather than a broken dropdown.
        // Results-zone chrome follows the STOREFRONT locale: it leads to a storefront page.
        // The string is rendered by Liquid through `| t`, so it stays in the locale files with
        // every other storefront string rather than being duplicated here.
        var allLabel = chromeString('view-all', pageLang() === 'ar' ? 'عرض كل نتائج البحث' : 'View all search results');
        self.resultZone.innerHTML =
          '<div class="drv-ls__empty"><a class="drv-ls__all" href="' + localeRoot() +
          'search?q=' + encodeURIComponent(q) + '&type=product">' + esc(allLabel) + '</a></div>';
      })
      .finally(function () {
        if (mySeq === self.seq) self.panel.removeAttribute('aria-busy');
      });
  };

  function init() {
    document.querySelectorAll('input[name="q"]').forEach(function (input) {
      if (input.dataset.drvLsBound) return;
      var form = input.closest('form');
      if (!form) return;
      input.dataset.drvLsBound = '1';
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-autocomplete', 'list');

      var panel = document.createElement('div');
      panel.className = 'drv-ls__panel';
      panel.hidden = true;
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-live', 'polite');
      panel.innerHTML = '<div class="drv-ls__suggest"></div><div class="drv-ls__results"></div>';

      // Anchor the dropdown to the field itself so the header stays visible behind it.
      var anchor = form.parentElement || form;
      if (getComputedStyle(anchor).position === 'static') anchor.style.position = 'relative';
      anchor.appendChild(panel);

      var staticPanel = anchor.querySelector('[data-predictive-search], .predictive-search');
      var ls = new LiveSearch(input, panel, staticPanel);

      form.addEventListener('submit', function () {
        var typed = (input.value || '').trim();
        if (!typed || !vocab) return;
        var rw = rewriteFor(typed);
        if (!rw || rw.kind !== 't') return;
        input.value = rw.target;
        var oq = form.querySelector('input[name="oq"]');
        if (!oq) { oq = document.createElement('input'); oq.type = 'hidden'; oq.name = 'oq'; form.appendChild(oq); }
        oq.value = typed;
      });

      // Keep the combobox state honest for assistive technology.
      new MutationObserver(function () {
        input.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
      }).observe(panel, { attributes: true, attributeFilter: ['hidden'] });

      return ls;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Header markup can arrive from a <template> when the search dialog opens.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('click', function () { setTimeout(init, 300); }, true);
})();

/**
 * Keep query-string filters alive across tag-path facet links.
 *
 * Our facets navigate by path (/collections/x/tag-a+tag-b) while Shopify's price facet uses
 * query params. A path-only href would silently discard an applied price range, so carry the
 * current query string onto every facet link. "Clear all" deliberately keeps its bare URL —
 * clearing everything is exactly what it promises.
 */
(function () {
  'use strict';
  function carryQuery() {
    var qs = window.location.search;
    if (!qs || qs.length < 2) return;
    document.querySelectorAll('.drv-facet__option, .drv-facets__chip').forEach(function (a) {
      if (a.dataset.drvQs) return;
      var href = a.getAttribute('href');
      if (!href || href.indexOf('?') !== -1) return;
      a.dataset.drvQs = '1';
      a.setAttribute('href', href + qs);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', carryQuery);
  } else {
    carryQuery();
  }
  document.addEventListener('shopify:section:load', carryQuery);
})();
